<template>
  <div>
    <!-- Stripe -->
    <div v-if="payment.stripe">
      <!-- Stripe payed -->
      <template v-if="paymentStripeId">
        <i class="fa fa-credit-card" /> Stripe<span class="hidden-print">
          - {{ paymentStripeId }}</span
        >
      </template>

      <!-- Stripe not payed -->
      <template v-else>
        <router-link :to="'/order/' + payment.id">
          <i class="fa fa-credit-card" /> Stripe
        </router-link>
        <span
          class="text-success cursor-pointer ml-05"
          @click="checkStripePayment"
        >
          <i class="fa fa-refresh" />
          <span v-if="isCheckingStripePayment"> Chargement...</span>
        </span>
      </template>

      <!-- Stripe dashboard search -->
      <a
        v-if="stripeSearchUrl"
        class="stripe-search ml-05"
        :href="stripeSearchUrl"
        target="_blank"
        rel="noopener noreferrer"
      >
        <i class="fa fa-search"></i>
      </a>
    </div>
    <!-- Stripe -->

    <!-- Avance immediate -->
    <template v-if="payment.isAI">
      <p>
        {{ payment.type }}
        <span v-if="payment.urssafPaymentIntent">
          <br />
          Statut :
          {{ payment.urssafPaymentIntent.statut || "Envoyé" }}
          <template v-if="payment.urssafPaymentIntent.dateVirement">
            <br />
            Date de virement :
            {{ formatDate(payment.urssafPaymentIntent.dateVirement) }}
          </template>
        </span>
      </p>

      <template v-if="client">
        <button
          v-if="!payment.urssafPaymentIntent"
          class="btn btn-sm btn-info"
          @click="sendPaymentIntent"
        >
          Envoyer la demande
        </button>
        <button v-else class="btn btn-sm btn-info" @click="checkPaymentIntent">
          Consulter le statut
        </button>
      </template>
    </template>
    <!-- Avance immediate -->

    <!-- Generic payments (chèques, CESU etc.) -->
    <template v-if="isGenericPayment">
      <div>
        {{ payment.type }}
        <button
          v-if="isAdmin($store.state.user)"
          class="btn btn-sm btn-link"
          @click="editType"
        >
          <i class="fa fa-edit" />
        </button>
      </div>

      <!-- Not payed -->
      <div
        v-if="
          hasPermission($store.state.user, 'PAYMENTS_WRITE') &&
          !payment.captured
        "
      >
        <!-- Create sessions -->
        <button
          v-if="!payment.pending && !isForBillingOnly"
          class="btn btn-sm btn-outline-warning"
          @click="allowSessions"
        >
          <i class="fa fa-user-plus"></i> Créer les séances
        </button>

        <!-- Set payed -->
        <button class="btn btn-sm btn-success" @click="capture">
          <i class="fa fa-check-circle-o"></i> Payé
        </button>
      </div>
    </template>
    <!-- Generic payments (chèques, CESU etc.) -->

    <!-- Refund payment -->
    <button v-if="showRefundButton" class="btn btn-sm btn-info" @click="refund">
      <i class="icon-reload"></i> Rembourser
    </button>
    <!-- Refund payment -->
  </div>
</template>

<script>
export default {
  props: {
    payment: {
      type: Object,
      required: true,
    },

    client: {
      type: Object,
      default: () => {},
    },

    isForBillingOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isCheckingStripePayment: false,
    };
  },

  computed: {
    paymentStripeId() {
      return (
        this.payment.stripe &&
        (this.payment.stripe.charge_id ||
          this.payment.stripe.balance_transaction ||
          this.payment.stripe.payment_intent)
      );
    },

    stripeSearchUrl() {
      if (!this.payment.stripe) {
        return null;
      }

      const query =
        this.payment.stripe.charge_id ||
        this.payment.stripe.balance_transaction ||
        this.payment.stripe.payment_intent ||
        "";

      if (!query) {
        return null;
      }

      return `https://dashboard.stripe.com/search?query=${query}`;
    },

    isGenericPayment() {
      return (
        this.payment.type !== "stripe" &&
        !this.payment.stripe &&
        !this.payment.isAI
      );
    },

    showRefundButton() {
      return (
        this.hasPermission(this.$store.state.user, "REFUNDS_WRITE") &&
        this.payment.captured &&
        (this.payment.stripe ||
          (this.isGenericPayment &&
            !this.payment.refunded &&
            this.payment.price > 0))
      );
    },
  },

  methods: {
    async checkStripePayment() {
      try {
        this.isCheckingStripePayment = true;

        await this.$api.post("/payments/fix-stripe-payment", {
          orderId: this.payment.orderId,
          paymentId: this.payment.id,
          paymentPrice: this.payment.price,
        });

        this.$emit("captured", this.payment);
      } catch (e) {
        this.$errorToast("Ce paiement n'a pas été trouvé coté Stripe");
      } finally {
        this.isCheckingStripePayment = false;
      }
    },

    async sendPaymentIntent() {
      this.$confirm({
        message:
          "Attention, voulez-vous vraiment envoyer la demande de paiement à l'urssaf ?",
        onConfirm: async () => {
          try {
            await this.$api.post("/urssaf/send-payment-intent", {
              clientId: this.client?.email,
              paymentId: this.payment.id,
            });

            window.location.reload();
          } catch (e) {
            this.$errorToast(
              "Impossible d'envoyer la demande de paiement à l'URSSAF",
            );
          }
        },
      });
    },

    async checkPaymentIntent() {
      try {
        await this.$api.post("/urssaf/check-payment-intent", {
          paymentId: this.payment.id,
        });

        window.location.reload();
      } catch (e) {
        this.$errorToast(
          "Impossible de vérifier la demande de paiement auprès de l'URSSAF",
        );
      }
    },

    editType() {
      this.$emit("editType", this.payment);
    },

    allowSessions() {
      this.$confirm({
        message: "Attention, cette action est irréversible !",
        onConfirm: async () => {
          try {
            await this.$api.put(`/payments/${this.payment.id}`, {
              pending: true,
            });

            this.$emit("pending", this.payment);
          } catch (e) {
            this.$errorToast("Impossible de créer les séances");
          }
        },
      });
    },

    capture() {
      this.$emit("capture", this.payment);
    },

    refund() {
      this.$emit("refund", this.payment);
    },
  },
};
</script>
